import { apiCall } from "@/api/client";
import { productionKeyHeader } from "@/api/tenants";
import type {
  ListMessagingCampaignsResponse,
  ListMessagingEmailsResponse,
  ListMessagingRecipientsResponse,
  MessagingCampaign,
  MessagingCampaignInput,
  MessagingEmail,
  MessagingPreview,
  MessagingPreviewProfile,
  MessagingStatus,
  MessagingTemplate,
  MessagingTemplateInput,
  MessagingTestResult,
} from "@/types/api";

export interface ListMessagingEmailsOptions {
  tenantId?: string;
  kind?: string;
  lastEvent?: string;
  cursor?: string | null;
  limit?: number;
}

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

export function listMessagingEmails(token: string, opts: ListMessagingEmailsOptions = {}) {
  const qs = query({
    tenant_id: opts.tenantId,
    kind: opts.kind,
    last_event: opts.lastEvent,
    cursor: opts.cursor,
    limit: opts.limit,
  });
  return apiCall<ListMessagingEmailsResponse>(`/messaging/emails${qs}`, { token });
}

export function getMessagingEmail(token: string, emailId: string) {
  return apiCall<MessagingEmail>(`/messaging/emails/${encodeURIComponent(emailId)}`, { token });
}

export function refreshMessagingEmail(token: string, emailId: string) {
  return apiCall<MessagingEmail>(
    `/messaging/emails/${encodeURIComponent(emailId)}/refresh`,
    { token, method: "POST" },
  );
}

export function getMessagingStatus(token: string) {
  return apiCall<MessagingStatus>("/messaging/status", { token });
}

export function listTemplates(token: string) {
  return apiCall<{ items: MessagingTemplate[] }>("/messaging/templates", { token });
}

export function getTemplate(token: string, slug: string) {
  return apiCall<MessagingTemplate>(`/messaging/templates/${encodeURIComponent(slug)}`, { token });
}

export function createTemplate(token: string, input: MessagingTemplateInput) {
  return apiCall<MessagingTemplate>("/messaging/templates", { token, method: "POST", body: input });
}

export function updateTemplate(token: string, slug: string, input: MessagingTemplateInput, updatedAt: number) {
  return apiCall<MessagingTemplate>(`/messaging/templates/${encodeURIComponent(slug)}`, {
    token,
    method: "PUT",
    body: { ...input, updatedAt },
  });
}

export function deleteTemplate(token: string, slug: string) {
  return apiCall<void>(`/messaging/templates/${encodeURIComponent(slug)}`, { token, method: "DELETE" });
}

export function previewTemplate(token: string, slug: string, profile: MessagingPreviewProfile) {
  return apiCall<MessagingPreview>(`/messaging/templates/${encodeURIComponent(slug)}/preview`, {
    token,
    method: "POST",
    body: { profile },
  });
}

export function sendTestTemplate(token: string, slug: string, profile: MessagingPreviewProfile) {
  return apiCall<MessagingTestResult>(`/messaging/templates/${encodeURIComponent(slug)}/test`, {
    token,
    method: "POST",
    body: { profile },
  });
}

export function listCampaigns(token: string, cursor?: string | null) {
  return apiCall<ListMessagingCampaignsResponse>(`/messaging/campaigns${query({ cursor, limit: 50 })}`, { token });
}

export function getCampaign(token: string, id: string) {
  return apiCall<MessagingCampaign>(`/messaging/campaigns/${encodeURIComponent(id)}`, { token });
}

export function createCampaign(token: string, input: MessagingCampaignInput) {
  return apiCall<MessagingCampaign>("/messaging/campaigns", { token, method: "POST", body: input });
}

export function updateCampaign(token: string, id: string, input: MessagingCampaignInput) {
  return apiCall<MessagingCampaign>(`/messaging/campaigns/${encodeURIComponent(id)}`, { token, method: "PUT", body: input });
}

export function deleteCampaign(token: string, id: string) {
  return apiCall<void>(`/messaging/campaigns/${encodeURIComponent(id)}`, { token, method: "DELETE" });
}

export function resolveCampaign(token: string, id: string) {
  return apiCall<MessagingCampaign>(`/messaging/campaigns/${encodeURIComponent(id)}/resolve`, { token, method: "POST" });
}

export function sendCampaign(token: string, id: string, productionKey?: string) {
  return apiCall<MessagingCampaign>(`/messaging/campaigns/${encodeURIComponent(id)}/send`, {
    token,
    method: "POST",
    body: { confirm: true },
    headers: productionKeyHeader(productionKey),
  });
}

export function cancelCampaign(token: string, id: string) {
  return apiCall<MessagingCampaign>(`/messaging/campaigns/${encodeURIComponent(id)}/cancel`, { token, method: "POST" });
}

export function listCampaignRecipients(token: string, id: string, opts: { status?: string; cursor?: string | null } = {}) {
  return apiCall<ListMessagingRecipientsResponse>(
    `/messaging/campaigns/${encodeURIComponent(id)}/recipients${query({ status: opts.status, cursor: opts.cursor, limit: 100 })}`,
    { token },
  );
}
